/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-add';
import { IAddressList, IAddressListItem, IAddressListProps } from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-list';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-show';
import { IAccountManagementAddressViewProps } from '@msdyn365-commerce-modules/address/src/modules/account-management-address/./account-management-address';
import {IAddressResource} from "../../../modules/custom-checkout-shipping-address/common";
import { IAccountManagementAddressResources } from "@msdyn365-commerce-modules/address/src/modules/account-management-address/account-management-address.props.autogenerated";

interface IAddressAddUpdatePropsExt extends IAddressAddUpdateProps {
    resources:IAddressResource;
    channelCountryRegionISOCode: string;
}

interface ICountryInput {
    key: string;
    value: string;
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({
    AddressDetail,
    items
}) => {

    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return (
                    <>
                        {item.description}
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Address List Component.
 * @param param0 - Root param.
 * @param param0.List - Address list.
 * @param param0.isShowList - Boolean.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressListItem[].
 * @returns - AddressListComponent Node.
 */
const AddressList: React.FC<IAddressList> = ({
    List,
    isShowList,
    heading,
    items
}) => {

    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items.map((item: IAddressListItem) => {
                const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                const { Error, isShowError, title, message } = error;
                return (<Node {...Item} key={key}>
                    <AddressShow {...showItems} />
                    {isShowError && <Node {...Error}>
                        {title}
                        {message}
                    </Node>}
                    {isShowPrimaryButton && primaryButton}
                    {editButton}
                    {removeButton}
                </Node>);
            })}
        </Node>
    );
};

/**
 * Address Lists Component.
 * @param param0 - Root param.
 * @param param0.ListAddress - List address.
 * @param param0.heading - List heading.
 * @param param0.isShowEmptyListMessage - Boolean.
 * @param param0.emptyListMessage - Empty list message.
 * @param param0.addButton - Add button.
 * @param param0.primaryAddressList - Primary address.
 * @param param0.otherAddressList - Other address.
 * @returns Address Lists Component Node.
 */
const AddressLists: React.FC<IAddressListProps> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {

    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} />
            <AddressList {...otherAddressList} />
        </Node>
    );
};

/**
 * AddressAddUpdateComponent.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */

const checkPhone = (addressPhoneRequiredErrorMessage: string) => {
    const saveAddressButton = document.querySelector('.msc-address-form__button-save.msc-btn') as HTMLButtonElement;
    const phone = document.getElementById('shipping_addressphone') as HTMLInputElement;
    if (phone) {
        const phoneCtn = phone.closest('.msc-address-form__item') as HTMLElement;
        const phoneAlert = phoneCtn.getElementsByClassName('msc-address-form__alert')[0];

        phone.setAttribute('aria-required', 'true');
        phone.setAttribute('type', 'tel');
        phone.setAttribute('maxlength', '15');
        if (phone.value.length === 0) {
            saveAddressButton.setAttribute('disabled', 'true');
        }
        phone.onkeyup = () => {
            if (phone.value.length < 1) {
                phoneCtn.classList.add('address-form__item-invalid');
                phoneAlert.textContent = addressPhoneRequiredErrorMessage;
                saveAddressButton.setAttribute('disabled', 'true');
            } else {
                phoneAlert.textContent === addressPhoneRequiredErrorMessage &&
                phoneCtn.classList.contains('address-form__item-invalid') &&
                phoneCtn.classList.remove('address-form__item-invalid');
                if (phoneAlert.textContent === addressPhoneRequiredErrorMessage) {
                    phoneAlert.textContent = '';
                }
                if (document.getElementsByClassName('address-form__item-invalid').length > 0) {
                    saveAddressButton.setAttribute('disabled', 'true');
                } else {
                    isRequiredIsFilled();
                }
            }
        };
        document
            .getElementsByClassName('msc-address-form')[0]
            .querySelectorAll('input, select')
            .forEach(function(element) {
                (element as HTMLInputElement).onchange = () => {
                    isRequiredIsFilled();
                };
            });
        const isRequiredIsFilled = () => {
            let allAreFilled = true;
            document
                .getElementsByClassName('msc-address-form')[0]
                .querySelectorAll("[aria-required = 'true']")
                .forEach(function(element) {
                    if (!allAreFilled) {
                        return;
                    }
                    if (!(element as HTMLInputElement).value) {
                        allAreFilled = false;
                        return;
                    }
                });
            if (allAreFilled && document.getElementsByClassName('address-form__item-invalid').length === 0 ) {
                saveAddressButton.removeAttribute('disabled');
            } else {
                saveAddressButton.setAttribute('disabled', 'true');
            }
        };
    }
};

const AddressAddUpdate: React.FC<IAddressAddUpdatePropsExt> = (props) => {
    const {
        AddressForm,
        heading,
        items,
        hasError,
        error,
        isShowSaveButton,
        saveButton,
        isShowCancelButton,
        cancelButton,
        channelCountryRegionISOCode
    } = props;
    // Phone Verifications
    React.useEffect(() => {
        checkPhone(props.resources.addressPhoneRequiredErrorMessage);
    }, [(document?.getElementById('shipping_addressphone') as HTMLInputElement)?.value]);

    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;

                const inputC = React.cloneElement(input as React.ReactElement, {});
                if(key === 'ThreeLetterISORegionName') {
                    if(channelCountryRegionISOCode === 'US') {
                        const contriesFiltered = inputC.props.displayData.filter((item:ICountryInput) => (item.key === 'USA'));
                        inputC.props.displayData = contriesFiltered;
                    } else if(channelCountryRegionISOCode === 'IT') {
                        const contriesFiltered = inputC.props.displayData.filter((item:ICountryInput) => (item.key === 'ITA' || item.key === 'SMR'));
                        inputC.props.displayData = contriesFiltered;
                    }
                }

                return (<Node {...AddressItem} key={key}>
                    {label}
                    {alert}
                    {inputC}
                </Node>);
            })}
            {hasError && <Node {...error.AddressError}>
                {error.title}
                {error.message}
            </Node>}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Account Management Address View Component.
 * @param props - Props.
 * @returns - AccountAddressManagement Module.
 */
interface IAccountManagementAddressViewPropsExt extends IAccountManagementAddressViewProps {
    resources:IAccountManagementAddressResources;
}
const AccountManagementAddressView: React.FC<IAccountManagementAddressViewPropsExt> = props => {
    const { AccountAddressManagement, viewState, showAddressList, showAddOrUpdateAddress, screenReaderNotification, resources } = props;
    const channelCountryRegionISOCode = props?.context?.request?.channel?.ChannelCountryRegionISOCode;
    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && <AddressLists {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate resources={resources} channelCountryRegionISOCode={channelCountryRegionISOCode} {...showAddOrUpdateAddress} />}
            {screenReaderNotification}
        </Module>
    );
};

export default AccountManagementAddressView;
